import {
  listWakeLoopProfiles,
  type WakeLoopProfile,
} from "../config/wakeloop-profiles-api";
import {
  isProfileKindAllowedInView,
  type SupportedCliView,
} from "../runtime/view-profile-guard";
import {
  promptSearchSelect,
  type SearchSelectResult,
} from "../ui/search-select";

export const SPACE_JOIN_PROFILE_SELECTION_MESSAGE =
  "Which profile should this join use?";
export const SPACE_COMMAND_PROFILE_SELECTION_MESSAGE =
  "Which profile should this space action use?";

export async function listVisibleWakeLoopProfilesForView(
  resolvedView: SupportedCliView
): Promise<WakeLoopProfile[]> {
  return (await listWakeLoopProfiles()).filter((profile) =>
    isProfileKindAllowedInView({
      resolvedView,
      profileKind: profile.profileKind,
    })
  );
}

export async function promptForWakeLoopProfileSelection(input: {
  message: string;
  resolvedView: SupportedCliView;
  visibleProfiles?: WakeLoopProfile[];
  currentProfileId?: string | null;
  backLabel?: string;
}): Promise<SearchSelectResult<string>> {
  const visibleProfiles =
    input.visibleProfiles ??
    (await listVisibleWakeLoopProfilesForView(input.resolvedView));
  return promptSearchSelect({
    message: input.message,
    items: buildWakeLoopProfilePromptItems({
      visibleProfiles,
      currentProfileId: input.currentProfileId ?? null,
    }),
    allowBack: true,
    backLabel: input.backLabel ?? "Cancel",
  });
}

function buildWakeLoopProfilePromptItems(input: {
  visibleProfiles: WakeLoopProfile[];
  currentProfileId: string | null;
}) {
  return moveCurrentProfileToFront(
    input.visibleProfiles,
    input.currentProfileId
  ).map((profile) => ({
    value: profile.wakeLoopProfileId,
    label: formatProfileSelectionLabel({
      profile,
      currentProfileId: input.currentProfileId,
    }),
    detail: formatProfileSelectionDetail(profile),
    searchText: [
      profile.profileName,
      profile.wakeLoopProfileId,
      profile.profileKind,
      profile.profileKind === "human" && profile.isDefault === true
        ? "main profile"
        : null,
    ]
      .filter((value): value is string => typeof value === "string")
      .join("\n"),
  }));
}

function formatProfileSelectionLabel(input: {
  profile: WakeLoopProfile;
  currentProfileId: string | null;
}): string {
  const suffixes: string[] = [];
  if (input.profile.wakeLoopProfileId === input.currentProfileId) {
    suffixes.push("current");
  }
  if (
    input.profile.profileKind === "human" &&
    input.profile.isDefault === true
  ) {
    suffixes.push("main");
  }
  if (suffixes.length === 0) {
    return input.profile.profileName;
  }
  return `${input.profile.profileName} (${suffixes.join(", ")})`;
}

function formatProfileSelectionDetail(profile: WakeLoopProfile): string {
  const segments = [profile.wakeLoopProfileId, profile.profileKind];
  if (profile.profileKind === "human" && profile.isDefault === true) {
    segments.push("main profile");
  }
  return segments.join(" · ");
}

function moveCurrentProfileToFront(
  profiles: WakeLoopProfile[],
  currentProfileId: string | null
): WakeLoopProfile[] {
  if (!currentProfileId) {
    return [...profiles];
  }

  const prioritizedProfiles: WakeLoopProfile[] = [];
  const remainingProfiles: WakeLoopProfile[] = [];

  for (const profile of profiles) {
    if (profile.wakeLoopProfileId === currentProfileId) {
      prioritizedProfiles.push(profile);
      continue;
    }
    remainingProfiles.push(profile);
  }

  return [...prioritizedProfiles, ...remainingProfiles];
}
